import React, { ChangeEvent, useRef, useState } from 'react';
import { Box, Chip, Fade, FormHelperText } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { PreviewContainer, UploadButton } from './formStyles';

/**
 * Garde-fou contre un dépôt absurde, PAS une contrainte serveur : multer n'a
 * aucun `limits.fileSize` (server.js : seul `fieldSize` est défini, et il ne
 * concerne que les champs texte). Le plafond est donc volontairement large —
 * un scan de bureau à 600 dpi pèse couramment 10 à 20 Mo, et refuser un
 * passeport rendrait infranchissable une étape obligatoire, sans que l'écran
 * n'offre le moindre moyen de compresser.
 *
 * La vraie limite doit être posée côté serveur, avec un 413 lisible.
 */
const TAILLE_MAX = 20 * 1024 * 1024;

// Volontairement sans HEIC : le backend n'a jamais reçu ce format et le consulat
// ne saurait pas l'ouvrir. Safari convertit d'ailleurs en JPEG dans la plupart
// des cas ; dans les autres, un refus explicite vaut mieux qu'un envoi illisible.
const TYPES_ACCEPTES = ['image/jpeg', 'image/png', 'application/pdf'];

interface Props {
  documentType: string;
  /**
   * Reçoit l'URL d'aperçu ET l'objet File.
   * Le File permet à l'appelant d'éviter le `fetch(blobUrl)` de reconversion, qui
   * échoue silencieusement sur iOS quand Safari purge la page pendant la prise de vue.
   */
  onDocumentSelected: (uri: string, fichier: File) => void;
  modeOptions: string[];
  icon: React.ReactNode;
  /** Document déjà enregistré (écran de modification). */
  currentUri?: string | null;
  /** Message d'erreur venant de la validation du formulaire. */
  erreur?: string;
}

/**
 * Sélecteur de document, partagé par VoyageurInsert et VoyageurUpdate qui en
 * portaient chacun une copie (~87 et ~91 lignes). Base : la version de
 * VoyageurUpdate, qui était le surensemble (prop currentUri, détection .pdf).
 *
 * Trois durcissements ajoutés : conservation de l'objet File, révocation de
 * l'URL d'aperçu précédente, et contrôle de taille et de type.
 */
const DocumentPicker: React.FC<Props> = ({
  documentType,
  onDocumentSelected,
  modeOptions,
  icon,
  currentUri,
  erreur,
}) => {
  const [preview, setPreview] = useState<string | null>(currentUri || null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  // URL créée par CE composant, à révoquer quand on la remplace. On ne révoque
  // pas au démontage : sous React.StrictMode le nettoyage est joué une première
  // fois à vide, et rendrait l'aperçu inutilisable.
  const urlCreee = useRef<string | null>(null);

  const acceptedTypes = modeOptions.includes('selectPdf') ? 'image/*,application/pdf' : 'image/*';

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    // Permet de resélectionner le même fichier après un refus.
    e.target.value = '';
    if (!file) return;

    if (file.size > TAILLE_MAX) {
      const mo = (file.size / (1024 * 1024)).toFixed(1);
      setRefus(`Fichier trop volumineux (${mo} Mo). La limite est de 20 Mo.`);
      return;
    }

    // Type DÉCLARÉ par le navigateur, pas les octets réels : cela écarte les
    // erreurs de sélection, pas un fichier malveillant. Le contrôle qui compte
    // reste côté serveur, où multer n'en fait aujourd'hui aucun.
    const typeAccepte =
      TYPES_ACCEPTES.includes(file.type) ||
      (file.type === '' && /\.(jpe?g|png|pdf)$/i.test(file.name));
    if (!typeAccepte) {
      setRefus('Format non accepté. Utilisez une image JPEG ou PNG, ou un PDF.');
      return;
    }

    setRefus(null);
    const fileURL = URL.createObjectURL(file);
    if (urlCreee.current) URL.revokeObjectURL(urlCreee.current);
    urlCreee.current = fileURL;

    setPreview(fileURL);
    setFileType(file.type);
    setFileName(file.name);
    onDocumentSelected(fileURL, file);
  };

  const message = refus ?? erreur;
  const estPdf = fileType === 'application/pdf' || (!!preview && preview.includes('.pdf'));

  return (
    <Box>
      <UploadButton
        variant="outlined"
        component="label"
        fullWidth
        startIcon={<CloudUploadIcon />}
        size="large"
        color={message ? 'error' : 'primary'}
      >
        {icon}
        <Box ml={1}>{preview ? `Modifier ${documentType}` : `Télécharger ${documentType}`}</Box>
        <input type="file" hidden accept={acceptedTypes} onChange={handleFileChange} />
      </UploadButton>

      {message && (
        <FormHelperText error sx={{ mt: 0.75, ml: 1.5 }}>
          {message}
        </FormHelperText>
      )}

      {preview && (
        <Fade in={true}>
          <PreviewContainer>
            {fileName && (
              <Box p={2} bgcolor="background.default">
                <Chip
                  icon={estPdf ? <PictureAsPdfIcon /> : <ImageIcon />}
                  label={fileName}
                  color="success"
                  variant="filled"
                  size="small"
                />
              </Box>
            )}
            {estPdf ? (
              <Box sx={{ width: '100%', height: 400 }}>
                <iframe
                  src={preview}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={`aperçu ${documentType}`}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  backgroundImage: `url(${preview})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: 'grey.100',
                }}
              />
            )}
          </PreviewContainer>
        </Fade>
      )}
    </Box>
  );
};

export default DocumentPicker;
